import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService
} from 'angular-auth-oidc-client';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map } from 'rxjs/operators';

import { Account } from '../core/models/AccountModel';

export abstract class AuthService {
  abstract userData$: Observable<Account | undefined>;
  abstract getToken(): string | undefined;
  abstract login(): Observable<void>;
  abstract logout(): void;
  abstract softLogout(): void;
}

@Injectable({ providedIn: 'root' })
export class AuthOIDCService implements AuthService {
  private accessToken?: string;

  constructor(
    private router: Router,
    private oidcEventService: PublicEventsService,
    private oidcSecurityService: OidcSecurityService
  ) {
    this.oidcEventService
      .registerForEvents()
      .pipe(filter(event => event.type === EventTypes.SilentRenewFailed))
      .subscribe(() => this.softLogout());

    this.oidcSecurityService.checkAuth().subscribe({
      next: ({ isAuthenticated }) => {
        if (isAuthenticated) {
          this.oidcSecurityService
            .getIdToken()
            .subscribe({ next: token => (this.accessToken = token) });
        }

        // Remove any authentication callback leftovers after authentication.
        // See https://code.siemens.com/core/api-self-service/-/issues/59
        setTimeout(() => {
          if (location.search) {
            location.search = '';
          }
        });
      },
      error: () => this.logout()
    });
  }

  userData$ = this.oidcSecurityService.userData$.pipe(
    map(({ userData }) =>
      userData
        ? {
          id: userData?.gid,
          title: `${userData?.given_name} ${userData?.family_name}`,
          email: userData?.email,
          company: userData?.org_code
        }
        : undefined
    )
  );

  getToken() {
    return this.accessToken;
  }

  login() {
    return this.oidcSecurityService
      .preloadAuthWellKnownDocument()
      .pipe(map(() => this.oidcSecurityService.authorize()));
  }

  logout() {
    this.oidcSecurityService
      .logoffAndRevokeTokens()
      .subscribe(() => this.router.navigate(['/']));
  }

  softLogout() {
    this.oidcSecurityService.logoffLocal();
    this.router.navigate(['/']);
  }
}
